import mapboxgl, {
  GeoJSONFeature,
  MapboxGeoJSONFeature,
  Point,
  PointLike,
} from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useState, useCallback } from "react";
import Map, { MapRef, Marker, Source, Layer } from "react-map-gl";
import { lang } from "src/language/Strings";
import "src/routes/companyId/Map/mapCss.css";
import { mapSettings } from "src/routes/companyId/Map/mapSettings";
import { InspectionResponse } from "src/types/InspectionUpdateRequest";
import { fixRingOrientation } from "src/utils/fixRingOrientation";

// Calculate the center point of a polygon geometry
function getPolygonCenter(geometry: any): [number, number] | null {
  if (!geometry || !geometry.coordinates) return null;

  let allCoords: number[][] = [];

  if (geometry.type === "Polygon") {
    allCoords = geometry.coordinates[0] || [];
  } else if (geometry.type === "MultiPolygon") {
    allCoords = geometry.coordinates.flat().flat();
  }

  if (allCoords.length === 0) return null;

  const sumLng = allCoords.reduce((sum, coord) => sum + coord[0], 0);
  const sumLat = allCoords.reduce((sum, coord) => sum + coord[1], 0);

  return [sumLng / allCoords.length, sumLat / allCoords.length];
}
function isInBoundingBox(bbox: [PointLike, PointLike], point: PointLike) {
  return (
    point[0] >= bbox[0][0] &&
    point[0] <= bbox[1][0] &&
    point[1] >= bbox[0][1] &&
    point[1] <= bbox[1][1]
  );
}

// Check if a point is inside a polygon using ray casting algorithm
function isPointInPolygon(
  point: [number, number],
  polygon: [number, number][]
): boolean {
  const [x, y] = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];

    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}

export function InspectionFormMap({
  setMapRef,
  item,
  onCoordinatesChange,
  onPolygonChange,
}: {
  setMapRef: (mapRef: MapRef) => void;
  item: InspectionResponse;
  onCoordinatesChange?: (longitude: number, latitude: number) => void;
  onPolygonChange?: (polygons: [number, number][][]) => void;
}) {
  const MapRefObject = useRef<MapRef | null>(null);
  const [clickCoordinates, setClickCoordinates] = useState<{
    lng: number;
    lat: number;
  } | null>(null);
  const [markerPosition, setMarkerPosition] = useState<{
    longitude: number;
    latitude: number;
  }>({
    longitude: item.longitude ?? 0,
    latitude: item.latitude ?? 0,
  });

  // Polygon state - now supports multiple polygons
  const [polygons, setPolygons] = useState<[number, number][][]>([]);
  const [currentPolygonPoints, setCurrentPolygonPoints] = useState<
    [number, number][]
  >([]);
  const [isDrawingPolygon, setIsDrawingPolygon] = useState(false);
  const [selectedPolygonIndex, setSelectedPolygonIndex] = useState<
    number | null
  >(null);
  const [draggedVertexIndex, setDraggedVertexIndex] = useState<number | null>(
    null
  );

  useEffect(() => {
    setMapRef(MapRefObject.current as MapRef);
  }, [setMapRef, MapRefObject.current]);
  // Set the center of the map to the item coordinates
  useEffect(() => {
    if (MapRefObject.current) {
      MapRefObject.current.flyTo({
        center: [item.longitude ?? 0, item.latitude ?? 0],
        zoom: 12,
      });
    }
  }, []);

  // Update marker position when item coordinates change
  useEffect(() => {
    setMarkerPosition({
      longitude: item.longitude ?? 0,
      latitude: item.latitude ?? 0,
    });
  }, [item.longitude, item.latitude]);

  // Polygon control functions
  const startDrawingPolygon = useCallback(() => {
    setIsDrawingPolygon(true);
    setCurrentPolygonPoints([]);
    setSelectedPolygonIndex(null);
  }, []);

  const finishDrawingPolygon = useCallback(() => {
    if (currentPolygonPoints.length >= 3) {
      const pol = [...currentPolygonPoints, currentPolygonPoints[0]];
      const fixedPol = fixRingOrientation(pol);
      let newPolygons = [...polygons, fixedPol];
      newPolygons = newPolygons.map((polygon) => fixRingOrientation(polygon));
      setPolygons(newPolygons);
      setCurrentPolygonPoints([]);
      setIsDrawingPolygon(false);
      setSelectedPolygonIndex(polygons.length); // Select the newly created polygon
      if (onPolygonChange) {
        onPolygonChange(newPolygons);
      }
    }
  }, [currentPolygonPoints.length, onPolygonChange, polygons.length]);

  const clearSelectedPolygon = useCallback(() => {
    if (selectedPolygonIndex !== null) {
      setPolygons((prev) =>
        prev.filter((_, index) => index !== selectedPolygonIndex)
      );
      setSelectedPolygonIndex(null);
      if (onPolygonChange) {
        onPolygonChange([]);
      }
    }
  }, [selectedPolygonIndex, onPolygonChange]);

  const clearAllPolygons = useCallback(() => {
    setPolygons([]);
    setCurrentPolygonPoints([]);
    setIsDrawingPolygon(false);
    setSelectedPolygonIndex(null);
    if (onPolygonChange) {
      onPolygonChange([]);
    }
  }, [onPolygonChange]);

  const selectPolygon = useCallback((index: number) => {
    setSelectedPolygonIndex(index);
  }, []);

  const handleMapClick = useCallback(
    (e: any) => {
      if (isDrawingPolygon) {
        const newPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        setCurrentPolygonPoints((prev) => [...prev, newPoint]);
      } else {
        setClickCoordinates({ lng: e.lngLat.lng, lat: e.lngLat.lat });
        // Check if click is inside any polygon to select it
        const clickPoint: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        for (let i = 0; i < polygons.length; i++) {
          if (isPointInPolygon(clickPoint, polygons[i])) {
            selectPolygon(i);
            return;
          }
        }
        // If no polygon was clicked, deselect
        setSelectedPolygonIndex(null);
      }
    },
    [isDrawingPolygon, polygons, selectPolygon]
  );

  const handleVertexDrag = useCallback(
    (index: number, e: any) => {
      setDraggedVertexIndex(index);
      if (selectedPolygonIndex !== null) {
        setPolygons((prev) =>
          prev.map((polygon, polyIndex) =>
            polyIndex === selectedPolygonIndex
              ? polygon.map((point, i) =>
                  i === index ? [e.lngLat.lng, e.lngLat.lat] : point
                )
              : polygon
          )
        );
      }
    },
    [selectedPolygonIndex]
  );

  const handleVertexDragEnd = useCallback(() => {
    setDraggedVertexIndex(null);
  }, []);

  return (
    <div className="relative">
      {/* Polygon Controls */}
      <div className="top-2 right-2 z-10 flex gap-2 justify-end">
        {isDrawingPolygon && currentPolygonPoints.length >= 3 && (
          <button
            onClick={finishDrawingPolygon}
            className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
          >
            {lang.finish}
          </button>
        )}
        {isDrawingPolygon && currentPolygonPoints.length < 3 && (
          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">
            {lang.drawingArea}
          </span>
        )}
        {!isDrawingPolygon && (
          <button
            onClick={startDrawingPolygon}
            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
            disabled={isDrawingPolygon}
          >
            {lang.drawArea}
          </button>
        )}
        {selectedPolygonIndex !== null && (
          <button
            onClick={clearSelectedPolygon}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            {lang.delete}
          </button>
        )}
        {selectedPolygonIndex === null && polygons.length > 0 && (
          <button
            onClick={clearAllPolygons}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            {lang.clear}
          </button>
        )}
      </div>

      <Map
        ref={MapRefObject}
        mapboxAccessToken={
          "pk.eyJ1IjoiZ3JlZW4tZGlnaXRhbGlzZXJpbmciLCJhIjoiY2xyZHdqZnJ1MTk3ODJqbXd1aW5qamFnNyJ9.H02WJFskLo_qSlnyZwwq0A"
        }
        id="inspection-form-map"
        mapStyle={mapSettings.backgroundMap}
        projection={{ name: "mercator" }}
        onClick={handleMapClick}
        cursor={isDrawingPolygon ? "crosshair" : "pointer"}
        style={{ width: "100%", height: "400px" }}
        initialViewState={{
          longitude: item.longitude ?? 0,
          latitude: item.latitude ?? 0,
          zoom: 12,
        }}
      >
        {(polygons.length === 0 || isDrawingPolygon) && (
          <Marker
            longitude={markerPosition.longitude}
            latitude={markerPosition.latitude}
            offset={[0, 0]}
            anchor="bottom"
            color="red"
            style={{ width: 20, height: 20 }}
            draggable={true}
            onClick={() => {
              setClickCoordinates({
                lng: markerPosition.longitude,
                lat: markerPosition.latitude,
              });
            }}
            onDrag={(e) => {
              setMarkerPosition({
                longitude: e.lngLat.lng,
                latitude: e.lngLat.lat,
              });
            }}
            onDragEnd={(e) => {
              const newCoords = {
                longitude: e.lngLat.lng,
                latitude: e.lngLat.lat,
              };
              setMarkerPosition(newCoords);
              setClickCoordinates({
                lng: newCoords.longitude,
                lat: newCoords.latitude,
              });
              // Call the callback to update parent component
              if (onCoordinatesChange) {
                onCoordinatesChange(newCoords.longitude, newCoords.latitude);
              }
            }}
          />
        )}

        {/* Polygon rendering - render all polygons */}
        {polygons.map((polygon, index) => (
          <Source
            key={`polygon-${index}`}
            id={`polygon-source-${index}`}
            type="geojson"
            data={{
              type: "Feature",
              geometry: {
                type: "Polygon",
                coordinates: [[...polygon, polygon[0]]], // Close the polygon
              },
              properties: { index },
            }}
          >
            <Layer
              id={`polygon-fill-${index}`}
              type="fill"
              paint={{
                "fill-color":
                  selectedPolygonIndex === index ? "#f97316" : "#3b82f6",
                "fill-opacity": selectedPolygonIndex === index ? 0.4 : 0.3,
              }}
            />
            <Layer
              id={`polygon-stroke-${index}`}
              type="line"
              paint={{
                "line-color":
                  selectedPolygonIndex === index ? "#ea580c" : "#1d4ed8",
                "line-width": selectedPolygonIndex === index ? 4 : 3,
                "line-opacity": 1,
              }}
            />
          </Source>
        ))}

        {/* Temporary line for drawing in progress */}
        {isDrawingPolygon && currentPolygonPoints.length > 1 && (
          <Source
            id="drawing-line-source"
            type="geojson"
            data={{
              type: "Feature",
              geometry: {
                type: "LineString",
                coordinates: currentPolygonPoints,
              },
              properties: {},
            }}
          >
            <Layer
              id="drawing-line"
              type="line"
              paint={{
                "line-color": "#1d4ed8",
                "line-width": 4,
                "line-opacity": 0.9,
                "line-dasharray": [2, 2],
              }}
            />
          </Source>
        )}

        {/* Vertex markers for resizing - only show for selected polygon */}
        {selectedPolygonIndex !== null &&
          polygons[selectedPolygonIndex]?.map((point, index) => (
            <Marker
              key={`vertex-${selectedPolygonIndex}-${index}`}
              longitude={point[0]}
              latitude={point[1]}
              draggable={true}
              onDrag={(e) => handleVertexDrag(index, e)}
              onDragEnd={handleVertexDragEnd}
            >
              <div
                className={`w-4 h-4 rounded-full border-2 ${
                  draggedVertexIndex === index
                    ? "bg-orange-600 border-orange-800"
                    : "bg-orange-400 border-orange-600"
                } cursor-move`}
                style={{
                  transform: "translate(-50%, -50%)",
                }}
              />
            </Marker>
          ))}
      </Map>
    </div>
  );
}
